import { useRef, useState } from 'react';
import { uploadImage } from '@/lib/api';

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  folder?: string;
}

export default function ImageUpload({ value, onChange, folder = 'gallery' }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadImage(file, folder);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {value && (
        <img
          src={value}
          alt="Preview"
          className="w-24 h-24 rounded-md object-cover object-top border border-background-200"
        />
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-outline whitespace-nowrap text-xs px-3 py-2 cursor-pointer"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
              Uploading...
            </>
          ) : (
            <>
              <i className="ri-upload-2-line" aria-hidden="true" />
              Upload Image
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste an image URL"
        className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
      />
      {error && <p className="text-red-600 text-xs">{error}</p>}
    </div>
  );
}